import {AlertDialog} from '@resurs-internal/mui/components';
import {AlertDialogProps} from '@resurs-internal/mui/components/AlertDialog/AlertDialog';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

interface AlertDialogState {
  variant?: AlertDialogProps['variant'];
  icon?: AlertDialogProps['icon'];
  header?: AlertDialogProps['header'];
  title?: AlertDialogProps['title'];
  subtitle?: AlertDialogProps['subtitle'];
  actions?: AlertDialogProps['actions'];
}

const AlertDialogContext = createContext<{
  error: (params: Omit<AlertDialogState, 'variant' | 'icon'>) => void;
  onClose: () => void;
  setState: (params: AlertDialogState | null) => void;
  state: AlertDialogState | null;
  success: (params: Omit<AlertDialogState, 'variant' | 'icon'>) => void;
  warning: (params: Omit<AlertDialogState, 'variant' | 'icon'>) => void;
}>({
  error: () => {
    /* noop */
  },
  onClose: () => {
    /* noop */
  },
  setState: () => {
    /* noop */
  },
  state: null,
  success: () => {
    /* noop */
  },
  warning: () => {
    /* noop */
  },
});

export const AlertDialogProvider: FC<PropsWithChildren> = ({children}) => {
  const [state, setState] = useState<AlertDialogState | null>(null);

  const handleClose = useCallback(() => {
    setState(null);
  }, []);

  const error = useCallback(
    (params: Omit<AlertDialogState, 'variant' | 'icon'>) =>
      setState({variant: 'error', icon: 'circle-exclamation', ...params}),
    [],
  );

  const success = useCallback(
    (params: Omit<AlertDialogState, 'variant' | 'icon'>) =>
      setState({variant: 'success', icon: 'circle-check', ...params}),
    [],
  );

  const warning = useCallback(
    (params: Omit<AlertDialogState, 'variant' | 'icon'>) =>
      setState({variant: 'warning', icon: 'circle-exclamation', ...params}),
    [],
  );

  return (
    <AlertDialogContext.Provider
      value={{state, setState, success, error, warning, onClose: handleClose}}>
      {children}
      <AlertDialog
        open={!!state}
        onClose={handleClose}
        variant={state?.variant}
        icon={state?.icon}
        header={state?.header}
        title={state?.title}
        subtitle={state?.subtitle}
        actions={state?.actions}
      />
    </AlertDialogContext.Provider>
  );
};

export const useAlertDialog = () => {
  return useContext(AlertDialogContext);
};
